export interface InlineImageOpenRequest {
  attachmentId: number;
  name: string;
}

export function attachmentIdFromInlineSrc(src: string): number | null {
  const match = /^sitescope-attachment:\/\/(\d+)\/?$/i.exec(src.trim());
  if (!match) {
    return null;
  }
  const id = Number(match[1]);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** The nth `<img>` in persisted HTML, when it is a saved attachment. */
export function inlineImageAtOrdinal(
  html: string,
  ordinal: number,
): InlineImageOpenRequest | null {
  if (!Number.isInteger(ordinal) || ordinal < 0) {
    return null;
  }
  const tags = html.match(/<img\b[^>]*>/gi) ?? [];
  const tag = tags.at(ordinal);
  if (!tag) {
    return null;
  }
  const src = /\bsrc=(["'])([^"']+)\1/i.exec(tag)?.[2] ?? '';
  const attachmentId = attachmentIdFromInlineSrc(src);
  if (attachmentId == null) {
    return null;
  }
  const alt = /\balt=(["'])([^"']*)\1/i.exec(tag)?.[2]?.trim() ?? '';
  return { attachmentId, name: alt || 'Image' };
}

/**
 * A tap on a rendered picture. A stamped `sitescope-attachment://` src wins,
 * and only when that file is still in the field HTML. Anything else uses the
 * picture's place among the images the sanitizer kept.
 */
export function inlineImageOpenFromPress(
  html: string,
  src: string,
  ordinal: number,
): InlineImageOpenRequest | null {
  const fromSrc = attachmentIdFromInlineSrc(src);
  if (fromSrc != null) {
    const tags = html.match(/<img\b[^>]*>/gi) ?? [];
    const index = tags.findIndex(tag => {
      const tagSrc = /\bsrc=(["'])([^"']+)\1/i.exec(tag)?.[2] ?? '';
      return attachmentIdFromInlineSrc(tagSrc) === fromSrc;
    });
    if (index < 0) {
      return null;
    }
    return inlineImageAtOrdinal(html, index);
  }
  return inlineImageAtOrdinal(html, ordinal);
}

/** An editor image node, when its stored src is a saved attachment. */
export function inlineImageOpenFromEditorNode(
  nodeName: string,
  src: unknown,
  alt: unknown,
): InlineImageOpenRequest | null {
  if (nodeName !== 'image' || typeof src !== 'string') {
    return null;
  }
  const attachmentId = attachmentIdFromInlineSrc(src);
  if (attachmentId == null) {
    return null;
  }
  const name = typeof alt === 'string' ? alt.trim() : '';
  return { attachmentId, name: name || 'Image' };
}

/**
 * Editor click on a saved picture. Returning true tells ProseMirror the click
 * was handled, so it does not move the caret or change the field.
 */
export function openEditorImageOnClick(
  nodeName: string,
  src: unknown,
  alt: unknown,
  event: { preventDefault(): void },
  opener: ((request: InlineImageOpenRequest) => void) | undefined,
): boolean {
  if (!opener) {
    return false;
  }
  const request = inlineImageOpenFromEditorNode(nodeName, src, alt);
  if (!request) {
    return false;
  }
  event.preventDefault();
  opener(request);
  return true;
}
